import React, { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Plus, Trash2, Save, Check, X } from "react-feather";
import { useApi } from "../hooks/useApi";
import { useRBAC } from "../hooks/useRBAC";
import Navigation from "./Navigation";
import Modal from "./Modal";
import LoadingSpinner from "./LoadingSpinner";
import MsspSignupApprovals from "./MsspSignupApprovals";
import SampleOrganizations from "./SampleOrganizations";
import InviteLinkBox from "./InviteLinkBox";
import LicenceCard from "./LicenceCard";
import EnzoicRelayClients from "./EnzoicRelayClients";
import SuperAdminFortinetPanel from "./SuperAdminFortinetPanel";
import SuperAdminKnowBe4Panel from "./SuperAdminKnowBe4Panel";
import RecentlyDeletedOrganizations from "./RecentlyDeletedOrganizations";
import { useRefetchFeatureFlags } from "../contexts/FeatureFlagsContext";
import { useRefetchLicenses } from "../contexts/LicenseContext";

const NOT_IN_LICENCE_HINT = "Not in this installation's licence";

const LICENSED_PRODUCTS = [
  { value: "Deals", label: "Deals" },
  { value: "Enterprise", label: "Enterprise" },
  { value: "BusinessUnits", label: "Business Units" },
  { value: "TPRM", label: "TPRM" },
  { value: "Ransomware", label: "Ransomware" },
  { value: "DarkWeb", label: "Dark Web" },
  { value: "AI", label: "AI" },
  { value: "EnterpriseDeals", label: "Enterprise Deals" },
  { value: "EnterpriseBusinessUnits", label: "Enterprise Business Units" },
  { value: "Applications", label: "Applications" },
  { value: "GRC", label: "GRC" },
];

// After a licence upload, the flags and licences are re-fetched once more this long afterwards, in
// case the immediate re-fetch reached a replica that had not yet refreshed its licence state.
export const LICENCE_LATE_REFETCH_MS = 60 * 1000;

// Per-org super-admin switches, one slim On/Off pulldown column each. `field` is the flag on the
// organizations list, `path` the PUT endpoint; `refetchFlags` re-reads the feature flags afterwards
// so the change shows for the selected organization without waiting for the poll.
//   Workflows / USOC: effective with the global feature flag; other users see it on their next reload.
//   Fusion data: effective with each global setting (projectors, store read).
//   Scorecard: no global flag.
export const FEATURE_TOGGLES = [
  {
    key: "workflows",
    header: "Workflows",
    name: "Remediation workflows",
    field: "remediationWorkflowsEnabled",
    path: "remediation-workflows",
    hint: "Remediation workflows for this organization (also needs the global feature flag)",
    error: "Failed to update remediation workflows",
  },
  {
    key: "usoc",
    header: "USOC",
    name: "Security Operations (USOC)",
    field: "usocEnabled",
    path: "usoc",
    hint: "Security Operations (USOC) for this organization (also needs the global feature flag)",
    error: "Failed to update USOC",
  },
  {
    key: "fusion",
    header: "Fusion",
    name: "Fusion connected data",
    field: "fusionConnectedDataEnabled",
    path: "fusion-connected-data",
    hint: "Relationship store for Fusion for this organization (also needs the global settings)",
    error: "Failed to update Fusion connected data",
    refetchFlags: true,
  },
  {
    key: "scorecard",
    header: "Scorecard",
    name: "Remediation scorecard",
    field: "remediationScorecardEnabled",
    path: "remediation-scorecard",
    hint: "Remediation Scorecard for this organization",
    error: "Failed to update remediation scorecard",
    refetchFlags: true,
  },
];

const thClass =
  "sticky top-0 z-10 bg-theme-secondary text-left px-4 py-3 text-sm font-medium text-content-secondary border-b border-border whitespace-nowrap";
const toggleThClass =
  "sticky top-0 z-10 bg-theme-secondary text-center px-1 py-3 text-xs font-medium text-content-secondary border-b border-border w-20";

const approvalStatusStyles = {
  Pending: "bg-yellow-100 text-yellow-800",
  Approved: "bg-green-100 text-green-800",
  Rejected: "bg-red-100 text-red-800",
};

// Pending orgs sort to the top so they surface for review; Rejected sink to the bottom.
const approvalSortOrder = (status) =>
  status === "Pending" ? 0 : status === "Rejected" ? 2 : 1;

const productColors = {
  Deals: "bg-blue-100 text-blue-800",
  Enterprise: "bg-green-100 text-green-800",
  BusinessUnits: "bg-yellow-100 text-yellow-800",
  TPRM: "bg-emerald-100 text-emerald-800",
  Ransomware: "bg-red-100 text-red-800",
  DarkWeb: "bg-indigo-100 text-indigo-800",
  AI: "bg-purple-100 text-purple-800",
  EnterpriseDeals: "bg-teal-100 text-teal-800",
  EnterpriseBusinessUnits: "bg-orange-100 text-orange-800",
  Applications: "bg-cyan-100 text-cyan-800",
  GRC: "bg-lime-100 text-lime-800",
};

const SuperAdmin = () => {
  const navigate = useNavigate();
  const apiCall = useApi();
  const { isSuperAdmin } = useRBAC();
  const refetchFlags = useRefetchFeatureFlags();
  const refetchLicenses = useRefetchLicenses();

  // Offline licence (GET /licence): drives the Licence card and, when a licence is required,
  // which products the grant modal offers. null until loaded or when the call fails.
  const [licenceInfo, setLicenceInfo] = useState(null);

  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal state
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLicenses, setModalLicenses] = useState([]);
  const [saving, setSaving] = useState(false);
  const [addProductValue, setAddProductValue] = useState("");

  // Delete org: one super admin requests it, a different one approves (typing Confirm).
  const [deleteRequestOrg, setDeleteRequestOrg] = useState(null);
  const [requestingDelete, setRequestingDelete] = useState(false);
  const [approveDeleteOrg, setApproveDeleteOrg] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  // Bumped after an approval so the Recently deleted card reloads.
  const [deletedReloadKey, setDeletedReloadKey] = useState(0);
  // False only where the deployment turns deletion approval off (staging): the trash button then
  // deletes after a typed Confirm. Assumed on until the server says otherwise.
  const [deletionApprovalRequired, setDeletionApprovalRequired] = useState(true);

  const [actioningOrgId, setActioningOrgId] = useState(null);

  // Fortinet diagnostics panel: the org it is open for, or null.
  const [fortinetOrg, setFortinetOrg] = useState(null);
  // KnowBe4 diagnostics panel: the org it is open for, or null.
  const [knowBe4Org, setKnowBe4Org] = useState(null);
  // Owner invite link from an approval while email is off: { id, url }. Held only in component
  // state (it is a credential). Shown after the approval's own reload; the next reload clears it.
  const [ownerLink, setOwnerLink] = useState(null);

  const loadOrganizations = useCallback(async () => {
    setOwnerLink(null);
    const data = await apiCall("GET", "/SuperAdmin/organizations");
    setOrganizations(
      (data || []).sort(
        (a, b) =>
          approvalSortOrder(a.approvalStatus) - approvalSortOrder(b.approvalStatus) ||
          a.organizationName.localeCompare(b.organizationName)
      )
    );
  }, [apiCall]);

  // Redirect if not super admin
  useEffect(() => {
    if (!isSuperAdmin()) {
      navigate("/dashboard");
    }
  }, [isSuperAdmin, navigate]);

  // Fetch organizations
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        setLoading(true);
        await loadOrganizations();
      } catch (err) {
        setError("Failed to load organizations");
      } finally {
        setLoading(false);
      }
    };
    if (isSuperAdmin()) {
      fetchOrganizations();
    }
  }, [isSuperAdmin, loadOrganizations]);

  // Fetch the installed licence once
  useEffect(() => {
    if (!isSuperAdmin()) return;
    let cancelled = false;
    apiCall("GET", "/licence")
      .then((data) => {
        if (!cancelled) setLicenceInfo(data || null);
      })
      .catch(() => {
        if (!cancelled) setLicenceInfo(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin, apiCall]);

  useEffect(() => {
    if (!isSuperAdmin()) return;
    let cancelled = false;
    apiCall("GET", "/SuperAdmin/deletion-settings")
      .then((data) => {
        if (!cancelled) setDeletionApprovalRequired(data?.approvalRequired !== false);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin, apiCall]);

  // One delayed re-fetch after an upload (see handleLicenceUploaded); cleared on unmount.
  const lateRefetchRef = useRef(null);
  useEffect(
    () => () => {
      if (lateRefetchRef.current) clearTimeout(lateRefetchRef.current);
    },
    []
  );

  // After an upload: show the new licence and refresh the banner status and the organization's
  // licensed modules without a page reload. Other replicas pick the licence up on their next
  // refresh, so the immediate re-fetch may land on one still holding the old state: re-fetch once
  // more after LICENCE_LATE_REFETCH_MS.
  const handleLicenceUploaded = async (data) => {
    if (data) setLicenceInfo(data);
    if (lateRefetchRef.current) clearTimeout(lateRefetchRef.current);
    lateRefetchRef.current = setTimeout(() => {
      lateRefetchRef.current = null;
      Promise.allSettled([refetchFlags(), refetchLicenses()]);
    }, LICENCE_LATE_REFETCH_MS);
    await Promise.allSettled([refetchFlags(), refetchLicenses()]);
  };

  // When a licence is required, only products in it (incl. derived helpers) can be granted, up to
  // the licence's per-organization allowance. effectiveProducts is null with no licence installed.
  const licenceRequired = licenceInfo?.required === true;
  const effectiveAllowances = new Map(
    (licenceInfo?.effectiveProducts || []).map((p) => [p.product, p.allowance])
  );
  const isProductInLicence = (product) => !licenceRequired || effectiveAllowances.has(product);
  const allowanceFor = (product) =>
    licenceRequired && effectiveAllowances.has(product) ? effectiveAllowances.get(product) : undefined;

  const openModal = (org) => {
    setSelectedOrg(org);
    setModalLicenses(
      (org.licenses || []).map((l) => ({
        ...l,
        _dirty: false,
        _deleted: false,
      }))
    );
    setAddProductValue("");
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedOrg(null);
    setModalLicenses([]);
  };

  const openDeleteRequest = (org) => {
    setDeleteRequestOrg(org);
    setDeleteConfirmText("");
  };

  const closeDeleteRequest = () => {
    if (requestingDelete) return;
    setDeleteRequestOrg(null);
  };

  const openApproveDelete = (org) => {
    setApproveDeleteOrg(org);
    setDeleteConfirmText("");
  };

  const closeApproveDelete = () => {
    if (deleting) return;
    setApproveDeleteOrg(null);
    setDeleteConfirmText("");
  };

  const handleNumAssessmentsChange = (index, value) => {
    setModalLicenses((prev) =>
      prev.map((l, i) =>
        i === index ? { ...l, numAssessments: parseInt(value) || 0, _dirty: true } : l
      )
    );
  };

  const handleDeleteLicense = (index) => {
    setModalLicenses((prev) =>
      prev.map((l, i) => (i === index ? { ...l, _deleted: true } : l))
    );
  };

  const handleAddLicense = () => {
    if (!addProductValue) return;
    // Check if already exists (and not deleted)
    const exists = modalLicenses.some(
      (l) => l.licensedProductId === addProductValue && !l._deleted
    );
    if (exists) return;

    setModalLicenses((prev) => [
      ...prev,
      {
        organizationLicensedProductId: null,
        licensedProductId: addProductValue,
        numAssessments: 999,
        _dirty: false,
        _deleted: false,
        _new: true,
      },
    ]);
    setAddProductValue("");
  };

  const handleSave = async () => {
    if (!selectedOrg) return;
    setSaving(true);
    setError(null);

    try {
      const orgId = selectedOrg.organizationId;

      // Process deletes
      for (const license of modalLicenses) {
        if (license._deleted && license.organizationLicensedProductId) {
          await apiCall(
            "DELETE",
            `/SuperAdmin/organizations/${orgId}/licenses/${license.organizationLicensedProductId}`
          );
        }
      }

      // Process creates
      for (const license of modalLicenses) {
        if (license._new && !license._deleted) {
          await apiCall("POST", `/SuperAdmin/organizations/${orgId}/licenses`, {
            licensedProductId: license.licensedProductId,
            numAssessments: license.numAssessments,
          });
        }
      }

      // Process updates
      for (const license of modalLicenses) {
        if (
          license._dirty &&
          !license._new &&
          !license._deleted &&
          license.organizationLicensedProductId
        ) {
          await apiCall(
            "PUT",
            `/SuperAdmin/organizations/${orgId}/licenses/${license.organizationLicensedProductId}`,
            { numAssessments: license.numAssessments }
          );
        }
      }

      // Refresh data
      await loadOrganizations();

      closeModal();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (org) => {
    setActioningOrgId(org.organizationId);
    setError(null);
    try {
      const res = await apiCall("POST", `/SuperAdmin/organizations/${org.organizationId}/approve`, null);
      await loadOrganizations();
      if (res?.ownerInviteUrl) {
        setOwnerLink({ id: org.organizationId, url: res.ownerInviteUrl });
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to approve organization");
    } finally {
      setActioningOrgId(null);
    }
  };

  const handleReject = async (org) => {
    setActioningOrgId(org.organizationId);
    setError(null);
    try {
      await apiCall("POST", `/SuperAdmin/organizations/${org.organizationId}/reject`, null);
      await loadOrganizations();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to reject organization");
    } finally {
      setActioningOrgId(null);
    }
  };

  // Writes only the org flag (super admin only); see FEATURE_TOGGLES for what else gates each one.
  const handleSetFeature = async (org, toggle, enabled) => {
    setActioningOrgId(org.organizationId);
    setError(null);
    try {
      await apiCall("PUT", `/SuperAdmin/organizations/${org.organizationId}/${toggle.path}`, { enabled });
      await loadOrganizations();
      if (toggle.refetchFlags) await refetchFlags();
    } catch (err) {
      setError(err.response?.data?.message || toggle.error);
    } finally {
      setActioningOrgId(null);
    }
  };

  // Records the request (the org stays live) and emails every super admin.
  const handleRequestDelete = async () => {
    if (!deleteRequestOrg) return;
    setRequestingDelete(true);
    setError(null);
    try {
      const res = await apiCall(
        "POST", `/SuperAdmin/organizations/${deleteRequestOrg.organizationId}/deletion-request`, null);
      if (res?.deleted) {
        // Approval is off on this deployment: the server deleted it at once.
        setOrganizations((prev) =>
          prev.filter((o) => o.organizationId !== deleteRequestOrg.organizationId)
        );
        setDeletedReloadKey((k) => k + 1);
      } else {
        await loadOrganizations();
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to request deletion");
    } finally {
      setRequestingDelete(false);
      setDeleteRequestOrg(null);
      setDeleteConfirmText("");
    }
  };

  const handleApproveDelete = async () => {
    if (!approveDeleteOrg) return;
    setDeleting(true);
    setError(null);
    try {
      await apiCall("POST", `/SuperAdmin/organizations/${approveDeleteOrg.organizationId}/deletion-request/approve`, null);
      setOrganizations((prev) =>
        prev.filter((o) => o.organizationId !== approveDeleteOrg.organizationId)
      );
      setDeletedReloadKey((k) => k + 1);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete organization");
    } finally {
      setDeleting(false);
      setApproveDeleteOrg(null);
      setDeleteConfirmText("");
    }
  };

  const handleCancelDelete = async (org) => {
    setActioningOrgId(org.organizationId);
    setError(null);
    try {
      await apiCall("DELETE", `/SuperAdmin/organizations/${org.organizationId}/deletion-request`);
      await loadOrganizations();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to cancel the deletion request");
    } finally {
      setActioningOrgId(null);
    }
  };

  if (!isSuperAdmin()) return null;
  if (loading) return <LoadingSpinner />;

  // Available products to add (not already in non-deleted list)
  const availableProducts = LICENSED_PRODUCTS.filter(
    (p) =>
      !modalLicenses.some(
        (l) => l.licensedProductId === p.value && !l._deleted
      )
  );

  return (
    <div className="min-h-screen bg-theme-primary">
      <Navigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-gradient-primary rounded-lg flex items-center justify-center">
            <Shield className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-content-primary">
              Super Admin - License Management
            </h1>
            <p className="text-sm text-content-secondary">
              Manage organization licenses and assessments
            </p>
          </div>
        </div>

        <LicenceCard licence={licenceInfo} onUploaded={handleLicenceUploaded} />

        <EnzoicRelayClients />

        {error && !modalOpen && (
          <div className="mb-4 p-3 bg-risk-critical/10 border border-risk-critical/30 rounded-lg text-risk-critical text-sm">
            {error}
          </div>
        )}

        {/* Organizations Table */}
        {/* The table scrolls inside its own box so the header row stays in view. */}
        <div className="bg-theme-secondary border border-border rounded-xl overflow-auto max-h-[75vh]">
          <table className="w-full">
            <thead>
              <tr>
                <th className={thClass}>Organization</th>
                <th className={thClass}>Org ID</th>
                <th className={thClass}>Status</th>
                <th className={thClass}>Licenses</th>
                {FEATURE_TOGGLES.map((t) => (
                  <th key={t.key} className={toggleThClass} title={t.name}>
                    {t.header}
                  </th>
                ))}
                <th className={thClass}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {organizations.map((org) => (
                <React.Fragment key={org.organizationId}>
                <tr
                  className="border-b border-border last:border-b-0 hover:bg-theme-hover transition-colors"
                >
                  <td className="px-4 py-4 text-sm text-content-primary font-medium">
                    {org.organizationName}
                  </td>
                  <td className="px-4 py-4 text-sm text-content-secondary">
                    {org.organizationId}
                  </td>
                  <td className="px-4 py-4">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        approvalStatusStyles[org.approvalStatus] ||
                        "bg-gray-100 text-gray-800"
                      }`}
                      title={org.approvalNote || undefined}
                    >
                      {org.approvalStatus || "Approved"}
                    </span>
                    {org.deletionRequestedAt && (
                      <span
                        className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"
                        title={`Requested ${new Date(org.deletionRequestedAt).toLocaleString()}`}
                      >
                        Deletion pending
                      </span>
                    )}
                    {org.deletionRequestedAt && (
                      <p className="mt-1 text-xs text-content-muted">
                        Requested by {org.deletionRequestedByMe ? "you" : org.deletionRequestedByName || "another super admin"}
                      </p>
                    )}
                    {org.approvalStatus === "Pending" && org.approvalNote && (
                      <p className="mt-1 text-xs text-content-muted max-w-xs">
                        {org.approvalNote}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex flex-wrap gap-1.5">
                      {(org.licenses || []).length === 0 ? (
                        <span className="text-sm text-content-muted italic">
                          No licenses
                        </span>
                      ) : (
                        (org.licenses || []).map((license) => (
                          <span
                            key={license.organizationLicensedProductId}
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                              productColors[license.licensedProductId] ||
                              "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {license.licensedProductId} ({license.numAssessments})
                          </span>
                        ))
                      )}
                    </div>
                  </td>
                  {FEATURE_TOGGLES.map((t) => (
                    <td key={t.key} className="px-1 py-4 text-center">
                      <select
                        value={org[t.field] ? "on" : "off"}
                        onChange={(e) => handleSetFeature(org, t, e.target.value === "on")}
                        disabled={actioningOrgId === org.organizationId}
                        className={`px-1.5 py-0.5 text-xs font-medium rounded-md border cursor-pointer disabled:opacity-50 ${
                          org[t.field]
                            ? "bg-green-100 text-green-800 border-green-300"
                            : "bg-gray-100 text-gray-800 border-gray-300"
                        }`}
                        aria-label={`${t.name} for ${org.organizationName}`}
                        title={t.hint}
                      >
                        <option value="on">On</option>
                        <option value="off">Off</option>
                      </select>
                    </td>
                  ))}
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      {(org.approvalStatus === "Pending" ||
                        org.approvalStatus === "Rejected") && (
                        <button
                          onClick={() => handleApprove(org)}
                          disabled={actioningOrgId === org.organizationId}
                          className="flex items-center gap-1 px-2.5 py-1 text-xs text-white bg-green-600 rounded-lg hover:bg-green-700 transition-all disabled:opacity-50"
                          title="Approve and send the invite email"
                        >
                          <Check className="h-3.5 w-3.5" />
                          Approve
                        </button>
                      )}
                      {org.approvalStatus === "Pending" && (
                        <button
                          onClick={() => handleReject(org)}
                          disabled={actioningOrgId === org.organizationId}
                          className="flex items-center gap-1 px-2.5 py-1 text-xs text-risk-critical border border-risk-critical/40 rounded-lg hover:bg-risk-critical/10 transition-all disabled:opacity-50"
                          title="Reject (no email sent)"
                        >
                          <X className="h-3.5 w-3.5" />
                          Reject
                        </button>
                      )}
                      <button
                        onClick={() => openModal(org)}
                        className="text-sm text-accent-cyan hover:text-accent-cyan/80 font-medium"
                      >
                        Manage
                      </button>
                      <button
                        onClick={() => setFortinetOrg(org)}
                        className="text-sm text-accent-cyan hover:text-accent-cyan/80 font-medium"
                        aria-label={`Fortinet diagnostics for ${org.organizationName}`}
                        title="Fortinet diagnostics"
                      >
                        Fortinet
                      </button>
                      <button
                        onClick={() => setKnowBe4Org(org)}
                        className="text-sm text-accent-cyan hover:text-accent-cyan/80 font-medium"
                        aria-label={`KnowBe4 diagnostics for ${org.organizationName}`}
                        title="KnowBe4 diagnostics"
                      >
                        KnowBe4
                      </button>
                      {org.deletionRequestedAt ? (
                        <>
                          <button
                            onClick={() => openApproveDelete(org)}
                            disabled={org.deletionRequestedByMe || actioningOrgId === org.organizationId}
                            className="flex items-center gap-1 px-2.5 py-1 text-xs whitespace-nowrap text-white bg-risk-critical rounded-lg hover:bg-risk-critical/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label={`Approve deletion of ${org.organizationName}`}
                            title={
                              org.deletionRequestedByMe
                                ? "A different super admin must approve your request"
                                : "Approve and delete this organization"
                            }
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Approve delete
                          </button>
                          <button
                            onClick={() => handleCancelDelete(org)}
                            disabled={actioningOrgId === org.organizationId}
                            className="text-sm whitespace-nowrap text-content-secondary hover:text-content-primary font-medium disabled:opacity-50"
                            aria-label={`Cancel deletion request for ${org.organizationName}`}
                          >
                            Cancel delete
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => openDeleteRequest(org)}
                          className="p-1.5 text-content-muted hover:text-risk-critical transition-colors"
                          aria-label={`${deletionApprovalRequired ? "Request deletion of" : "Delete"} ${org.organizationName}`}
                          title={
                            deletionApprovalRequired
                              ? "Request deletion (another super admin must approve)"
                              : "Delete organization"
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {ownerLink?.id === org.organizationId && (
                  <tr className="border-b border-border last:border-b-0">
                    <td colSpan={5 + FEATURE_TOGGLES.length} className="px-6 py-4">
                      <div className="flex items-start gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-content-secondary">
                            Email is off on this install. Send this link to the organization owner; they must sign in with the email address they signed up with.
                          </p>
                          <InviteLinkBox url={ownerLink.url} compact />
                        </div>
                        <button
                          type="button"
                          onClick={() => setOwnerLink(null)}
                          className="p-1 text-content-muted hover:text-content-primary rounded transition-colors"
                          aria-label="Dismiss invite link"
                          title="Dismiss"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
              {organizations.length === 0 && (
                <tr>
                  <td
                    colSpan={5 + FEATURE_TOGGLES.length}
                    className="px-6 py-8 text-center text-content-muted"
                  >
                    No organizations found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <SampleOrganizations />

        <MsspSignupApprovals />

        <RecentlyDeletedOrganizations reloadKey={deletedReloadKey} onRestored={loadOrganizations} />
      </div>

      {/* Request deletion: the org stays live until a different super admin approves. With approval
          off (staging) this is a direct delete behind a typed Confirm. */}
      <Modal
        isOpen={!!deleteRequestOrg}
        onClose={closeDeleteRequest}
        title={deletionApprovalRequired ? "Request Organization Deletion" : "Delete Organization"}
        size="md"
      >
        <div>
          {deletionApprovalRequired ? (
            <>
              <p className="text-sm text-content-secondary mb-3">
                Request deletion of{" "}
                <span className="font-semibold text-content-primary">
                  {deleteRequestOrg?.organizationName}
                </span>
                ?
              </p>
              <p className="text-sm text-content-secondary mb-6">
                Every super admin will get an email. The organization stays live until a different super
                admin approves the deletion.
              </p>
            </>
          ) : (
            <>
              <p className="text-sm text-content-secondary mb-4">
                Delete{" "}
                <span className="font-semibold text-content-primary">
                  {deleteRequestOrg?.organizationName}
                </span>
                ? Deletion approval is off on this deployment. Type{" "}
                <span className="font-semibold text-content-primary">Confirm</span> to delete. The
                organization can be restored from Recently deleted for 30 days.
              </p>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type 'Confirm' here"
                className="w-full px-3 py-2 border border-border rounded-md bg-input-bg text-content-primary focus:outline-none focus:ring-2 focus:ring-risk-critical/50 mb-4"
                autoFocus
              />
            </>
          )}
          <div className="flex justify-end gap-3">
            <button
              onClick={closeDeleteRequest}
              disabled={requestingDelete}
              className="px-4 py-2 text-sm text-content-secondary hover:text-content-primary bg-transparent border border-border rounded-lg hover:bg-theme-hover transition-all"
            >
              Go Back
            </button>
            <button
              onClick={handleRequestDelete}
              disabled={requestingDelete || (!deletionApprovalRequired && deleteConfirmText !== "Confirm")}
              className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-risk-critical rounded-lg hover:bg-risk-critical/90 transition-all disabled:opacity-50"
            >
              {deletionApprovalRequired
                ? requestingDelete ? "Requesting..." : "Request deletion"
                : requestingDelete ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Approve deletion (a different super admin from the requester) */}
      <Modal
        isOpen={!!approveDeleteOrg}
        onClose={closeApproveDelete}
        title="Approve Deletion"
        size="md"
      >
        <div>
          <p className="text-sm text-content-secondary mb-4">
            {approveDeleteOrg?.deletionRequestedByName || "Another super admin"} asked to delete{" "}
            <span className="font-semibold text-content-primary">
              {approveDeleteOrg?.organizationName}
            </span>
            . Type <span className="font-semibold text-content-primary">Confirm</span> to approve. The
            organization can be restored from Recently deleted for 30 days.
          </p>
          <input
            type="text"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder="Type 'Confirm' here"
            className="w-full px-3 py-2 border border-border rounded-md bg-input-bg text-content-primary focus:outline-none focus:ring-2 focus:ring-risk-critical/50 mb-4"
            autoFocus
          />
          <div className="flex justify-end gap-3">
            <button
              onClick={closeApproveDelete}
              disabled={deleting}
              className="px-4 py-2 text-sm text-content-secondary hover:text-content-primary bg-transparent border border-border rounded-lg hover:bg-theme-hover transition-all"
            >
              Go Back
            </button>
            <button
              onClick={handleApproveDelete}
              disabled={deleteConfirmText !== "Confirm" || deleting}
              className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-risk-critical rounded-lg hover:bg-risk-critical/90 transition-all disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4" />
              {deleting ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Fortinet diagnostics panel (last scan + trace switch) */}
      {fortinetOrg && (
        <SuperAdminFortinetPanel org={fortinetOrg} onClose={() => setFortinetOrg(null)} />
      )}

      {/* KnowBe4 diagnostics panel (last sync, test connection, detailed logging) */}
      {knowBe4Org && (
        <SuperAdminKnowBe4Panel org={knowBe4Org} onClose={() => setKnowBe4Org(null)} />
      )}

      {/* License Management Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={`Manage Licenses - ${selectedOrg?.organizationName || ""}`}
        size="lg"
      >
        {error && (
          <div className="mb-4 p-3 bg-risk-critical/10 border border-risk-critical/30 rounded-lg text-risk-critical text-sm">
            {error}
          </div>
        )}

        {/* Current Licenses */}
        <div className="space-y-3 mb-6">
          {modalLicenses.filter((l) => !l._deleted).length === 0 ? (
            <p className="text-sm text-content-muted italic">
              No licenses assigned
            </p>
          ) : (
            modalLicenses
              .filter((l) => !l._deleted)
              .map((license, idx) => {
                const realIndex = modalLicenses.indexOf(license);
                return (
                  <div
                    key={license.organizationLicensedProductId || `new-${idx}`}
                    className="flex items-center gap-3 p-3 bg-theme-primary rounded-lg border border-border"
                  >
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium min-w-[100px] justify-center ${
                        productColors[license.licensedProductId] ||
                        "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {LICENSED_PRODUCTS.find(
                        (p) => p.value === license.licensedProductId
                      )?.label || license.licensedProductId}
                    </span>
                    <div className="flex items-center gap-2 flex-1">
                      <label className="text-sm text-content-secondary whitespace-nowrap">
                        Assessments:
                      </label>
                      <input
                        type="number"
                        value={license.numAssessments}
                        onChange={(e) =>
                          handleNumAssessmentsChange(realIndex, e.target.value)
                        }
                        className="w-24 px-2 py-1 rounded-md bg-input-bg border border-border text-content-primary text-sm focus:outline-none focus:ring-2 focus:ring-accent-cyan/50"
                        min="0"
                        max={allowanceFor(license.licensedProductId)}
                        aria-label={`Assessments for ${license.licensedProductId}`}
                      />
                    </div>
                    <button
                      onClick={() => handleDeleteLicense(realIndex)}
                      className="p-1.5 text-content-muted hover:text-risk-critical transition-colors"
                      title="Remove license"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                );
              })
          )}
        </div>

        {/* Add License */}
        {availableProducts.length > 0 && (
          <div className="flex items-center gap-2 mb-6 p-3 bg-theme-primary/50 rounded-lg border border-border/50">
            <select
              value={addProductValue}
              onChange={(e) => setAddProductValue(e.target.value)}
              aria-label="Product to add"
              className="flex-1 px-3 py-2 rounded-lg bg-input-bg border border-border text-content-primary text-sm focus:outline-none focus:ring-2 focus:ring-accent-cyan/50"
            >
              <option value="">Select a product to add...</option>
              {availableProducts.map((p) => {
                const inLicence = isProductInLicence(p.value);
                return (
                  <option
                    key={p.value}
                    value={p.value}
                    disabled={!inLicence}
                    title={inLicence ? undefined : NOT_IN_LICENCE_HINT}
                  >
                    {inLicence ? p.label : `${p.label} (${NOT_IN_LICENCE_HINT})`}
                  </option>
                );
              })}
            </select>
            <button
              onClick={handleAddLicense}
              disabled={!addProductValue}
              className="flex items-center gap-1 px-3 py-2 bg-gradient-primary text-white text-sm rounded-lg hover:shadow-glow-cyan transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="h-4 w-4" />
              Add
            </button>
          </div>
        )}

        {/* Save / Cancel */}
        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <button
            onClick={closeModal}
            className="px-4 py-2 text-sm text-content-secondary hover:text-content-primary bg-transparent border border-border rounded-lg hover:bg-theme-hover transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-gradient-primary rounded-lg hover:shadow-glow-cyan transition-all disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default SuperAdmin;
